import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './CreditCalculator.css';
import SavedCalculations from './SavedCalculations';
import OfferPreview from './OfferPreview';
import FEISGenerator from './FEISGenerator';
import PaymentSchedule from './PaymentSchedule';
import ErrorBoundary from '../ErrorBoundary';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Funcții helper pentru formatare românească
const formatAmount = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const num = typeof value === 'string' ? parseInt(value.replace(/\./g, ''), 10) : value;
  if (isNaN(num)) return '';
  return num.toLocaleString('ro-RO');
};

const parseAmount = (value) => {
  if (!value || value === '') return '';
  const cleaned = String(value).replace(/\./g, '').replace(/,/g, '');
  return cleaned;
};

const isValidAmountInput = (value) => {
  if (value === '') return true;
  return /^[0-9.]*$/.test(value);
};

const buildProductType = (data) =>
  data.creditType === 'refinance'
    ? `refinance_${(data.energyClass || 'BC').toLowerCase()}`
    : `energy_class_${(data.energyClass || 'BC').toLowerCase()}`;

const CreditCalculator = () => {
  const [formData, setFormData] = useState({
    loanAmount: '',
    loanPeriodMonths: '',
    creditType: 'purchase',  // purchase sau refinance
    energyClass: 'BC',       // A, BC, GREEN
    interestType: 'fixed_5y',
    irccRate: 5.57,
    incomeCollection: 'with_income',
    downPayment: 25,
    downPaymentValue: '',    // Avans în RON (opțional)
    lifeInsurance: 'mandatory',
    propertyPrice: '',
    propertyEvaluation: '',
    clientIncome: '',
    familyIncome: ''
  });

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [evaluationTouched, setEvaluationTouched] = useState(false);
  const [selectedBanks, setSelectedBanks] = useState(new Set());
  const [sortFilter, setSortFilter] = useState('');
  const [isRealTimeUpdate, setIsRealTimeUpdate] = useState(false);
  const [clientName, setClientName] = useState('');
  const [saveCalculation, setSaveCalculation] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showFEIS, setShowFEIS] = useState(false);
  const [selectedOfferForFEIS, setSelectedOfferForFEIS] = useState(null);
  const [activeTab, setActiveTab] = useState('calculator');
  const [savingManually, setSavingManually] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveClientName, setSaveClientName] = useState('');
  
  // State pentru Scadențar Modal
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleResult, setScheduleResult] = useState(null);

  // 1. TIPUL PRODUSULUI - Credit Ipotecar sau Refinanțare
  const creditTypes = [
    { value: 'purchase', label: 'Credit Ipotecar', icon: '🏠', color: 'green' },
    { value: 'refinance', label: 'Refinanțare Credit Ipotecar', icon: '🔄', color: 'blue' }
  ];

  // 2. CLASA ENERGETICĂ
  const energyClassTypes = [
    { value: 'A', label: 'Clasa A', icon: '🌟', color: 'green', description: 'Eficiență maximă' },
    { value: 'BC', label: 'Clasa B/C', icon: '🏠', color: 'blue', description: 'Standard' },
    { value: 'GREEN', label: 'Credit Verde RoGBC', icon: '🌿', color: 'emerald', description: 'Romania Green Building Council' }
  ];

  // 3. TIPUL DOBÂNZII
  const interestTypes = [
    { value: 'fixed_2y', label: '2 Ani Fixă', icon: '2️⃣' },
    { value: 'fixed_3y', label: '3 Ani Fixă', icon: '3️⃣' },
    { value: 'fixed_5y', label: '5 Ani Fixă', icon: '5️⃣' },
    { value: 'fixed_10y', label: '10 Ani Fixă', icon: '🔟' },
    { value: 'fixed_full', label: 'Fixă Toată Perioada', icon: '♾️' },
    { value: 'variable', label: 'Variabilă', icon: '📊' }
  ];

  // 4. ÎNCASARE VENIT
  const incomeCollectionTypes = [
    { value: 'with_income', label: 'Cu Încasare Venit', icon: '💳', color: 'green' },
    { value: 'without_income', label: 'Fără Încasare Venit', icon: '🚫', color: 'red' }
  ];

  // 5. AVANS
  const downPaymentOptions = [
    { value: 15, label: '15%' },
    { value: 19, label: '<20%' },
    { value: 20, label: '≥20%' },
    { value: 25, label: '25%' },
    { value: 30, label: '20-40%' },
    { value: 40, label: '≥40%' }
  ];

  // 6. ASIGURARE VIAȚĂ
  const lifeInsuranceTypes = [
    { value: 'mandatory', label: 'Obligatorie', icon: '🔒', color: 'red' },
    { value: 'optional', label: 'Opțională', icon: '✨', color: 'green' }
  ];

  const sortFilters = [
    { value: 'lowest_fixed_rate', label: 'Cea mai mică rată fixă', icon: '⬇️' },
    { value: 'lowest_variable_rate', label: 'Cea mai mică rată variabilă', icon: '📉' },
    { value: 'lowest_costs', label: 'Cele mai mici costuri', icon: '💰' }
  ];

  // Fetch IRCC on mount
  useEffect(() => {
    const fetchIRCC = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/ircc`);
        if (response.data.ircc_rate) {
          setFormData(prev => ({ ...prev, irccRate: response.data.ircc_rate }));
        }
      } catch (err) {
        console.log('Using default IRCC rate');
      }
    };
    fetchIRCC();
  }, []);

  // Baza de calcul avans: min(evaluare, pret_imobil)
  const getBaseValue = (data) => {
    const price = parseFloat(data.propertyPrice) || 0;
    const evaluation = parseFloat(data.propertyEvaluation) || 0;
    if (price > 0 && evaluation > 0) return Math.min(price, evaluation);
    if (price > 0) return price;
    if (evaluation > 0) return evaluation;
    return 0;
  };

  // Determina butonul de avans corespunzator procentului
  const getAutoDownPaymentButton = (percent) => {
    if (percent >= 40) return 40;
    if (percent >= 25) return 25;
    if (percent >= 20) return 20;
    return 15;
  };

  // Cand se selecteaza un buton de avans % -> recalculeaza credit si avans in RON
  const handleDownPaymentSelect = (percent) => {
    const base = getBaseValue(formData);
    if (base > 0) {
      const downValue = Math.round(base * percent / 100);
      const loanAmount = Math.round(base - downValue);
      setFormData(prev => ({
        ...prev,
        downPayment: percent,
        downPaymentValue: String(downValue),
        loanAmount: String(loanAmount > 0 ? loanAmount : 0)
      }));
    } else {
      setFormData(prev => ({ ...prev, downPayment: percent }));
    }
  };

  // Cand se introduce manual avansul in RON -> recalculeaza credit si auto-selecteaza butonul
  const handleDownPaymentValueChange = (val) => {
    const rawVal = parseAmount(val);
    const base = getBaseValue(formData);
    if (base > 0 && rawVal) {
      const numVal = parseFloat(rawVal);
      const percent = (numVal / base) * 100;
      const autoButton = getAutoDownPaymentButton(percent);
      const loanAmount = Math.round(base - numVal);
      setFormData(prev => ({
        ...prev,
        downPaymentValue: rawVal,
        downPayment: autoButton,
        loanAmount: String(loanAmount > 0 ? loanAmount : 0)
      }));
    } else {
      setFormData(prev => ({ ...prev, downPaymentValue: rawVal }));
    }
  };

  // Recalculeaza cand se schimba pretul imobilului sau evaluarea
  useEffect(() => {
    const base = getBaseValue(formData);
    if (base > 0) {
      const downValue = Math.round(base * formData.downPayment / 100);
      const loanAmount = Math.round(base - downValue);
      setFormData(prev => ({
        ...prev,
        downPaymentValue: String(downValue),
        loanAmount: String(loanAmount > 0 ? loanAmount : 0)
      }));
    }
  }, [formData.propertyPrice, formData.propertyEvaluation]); // eslint-disable-line

  // Real-time updates
  useEffect(() => {
    if (formData.loanAmount && formData.loanPeriodMonths && results.length > 0) {
      const timeoutId = setTimeout(() => {
        handleRealTimeUpdate(formData);
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [formData.creditType, formData.energyClass, formData.interestType, formData.incomeCollection, 
      formData.downPayment, formData.lifeInsurance, formData.irccRate,
      formData.clientIncome, formData.familyIncome]);

  const handleRealTimeUpdate = async (data) => {
    if (!data.loanAmount || !data.loanPeriodMonths || isRealTimeUpdate) return;
    if (Number.isNaN(parseFloat(data.irccRate))) return;
    
    setIsRealTimeUpdate(true);
    
    try {
      const token = localStorage.getItem('token');
      const cleanAmount = (val) => val ? parseFloat(String(val).replace(/\./g, '')) : 0;
      
      // Construiește product_type din creditType + energyClass
      const productType = data.creditType === 'refinance' ? `refinance_${data.energyClass.toLowerCase()}` : `energy_class_${data.energyClass.toLowerCase()}`;
      
      // Calculează avansul - prioritate: valoare RON > procent selectat; bază = min(Preț, Evaluare)
      let downPaymentPercent = data.downPayment;
      let downPaymentType = 'percent';
      const loanAmount = cleanAmount(data.loanAmount);
      const propertyPrice = cleanAmount(data.propertyPrice);
      const downPaymentValue = cleanAmount(data.downPaymentValue);
      const baseValue = getBaseValue(data);
      
      if (downPaymentValue > 0 && baseValue > 0) {
        // Avans în RON raportat la minimul dintre preț și evaluare
        downPaymentPercent = (downPaymentValue / baseValue) * 100;
      } else if (baseValue > 0) {
        // Folosim procentul selectat din buton
        downPaymentPercent = data.downPayment;
      }
      
      const response = await axios.post(
        `${API_URL}/api/calculator/calculate`,
        {
          loan_amount: loanAmount,
          loan_period_months: parseInt(data.loanPeriodMonths),
          product_type: productType,
          interest_type: data.interestType,
          ircc_rate: parseFloat(data.irccRate),
          income_collection: data.incomeCollection,
          down_payment: downPaymentPercent,
          down_payment_type: downPaymentType,
          property_price: propertyPrice || null,
          property_evaluation: cleanAmount(data.propertyEvaluation) || null,
          life_insurance: data.lifeInsurance,
          client_income: cleanAmount(data.clientIncome) || null,
          family_income: cleanAmount(data.familyIncome) || null,
          energy_class: data.energyClass || 'BC'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setResults(response.data);
    } catch (error) {
      console.error('Real-time update error:', error);
    } finally {
      setIsRealTimeUpdate(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const cleanAmount = (val) => val ? parseFloat(String(val).replace(/\./g, '')) : 0;
      
      // Construiește product_type din creditType + energyClass
      const productType = formData.creditType === 'refinance' ? `refinance_${formData.energyClass.toLowerCase()}` : `energy_class_${formData.energyClass.toLowerCase()}`;
      
      // Calculează avansul - prioritate: valoare RON > procent selectat; bază = min(Preț, Evaluare)
      let downPaymentPercent = formData.downPayment;
      let downPaymentType = 'percent';
      const loanAmount = cleanAmount(formData.loanAmount);
      const propertyPrice = cleanAmount(formData.propertyPrice);
      const downPaymentValue = cleanAmount(formData.downPaymentValue);
      const baseValue = getBaseValue(formData);
      
      if (downPaymentValue > 0 && baseValue > 0) {
        // Avans în RON raportat la minimul dintre preț și evaluare
        downPaymentPercent = (downPaymentValue / baseValue) * 100;
      } else if (baseValue > 0) {
        // Folosim procentul selectat din buton
        downPaymentPercent = formData.downPayment;
      }
      
      const response = await axios.post(
        `${API_URL}/api/calculator/calculate`,
        {
          loan_amount: loanAmount,
          loan_period_months: parseInt(formData.loanPeriodMonths),
          product_type: productType,
          interest_type: formData.interestType,
          ircc_rate: parseFloat(formData.irccRate),
          income_collection: formData.incomeCollection,
          down_payment: downPaymentPercent,
          down_payment_type: downPaymentType,
          property_price: propertyPrice || null,
          property_evaluation: cleanAmount(formData.propertyEvaluation) || null,
          life_insurance: formData.lifeInsurance,
          client_income: cleanAmount(formData.clientIncome) || null,
          family_income: cleanAmount(formData.familyIncome) || null,
          energy_class: formData.energyClass || 'BC',
          client_name: saveCalculation ? clientName : null,
          save_calculation: saveCalculation && clientName
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setResults(response.data);
      setSortFilter('');
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const applySortFilter = (filter) => {
    if (results.length === 0) return;
    
    setSortFilter(filter);
    let sortedResults = [...results];
    
    switch (filter) {
      case 'lowest_fixed_rate':
        sortedResults.sort((a, b) => (a.fixed_rate || 999) - (b.fixed_rate || 999));
        break;
      case 'lowest_variable_rate':
        sortedResults.sort((a, b) => (a.variable_rate || 999) - (b.variable_rate || 999));
        break;
      case 'lowest_costs':
        sortedResults.sort((a, b) => a.total_costs - b.total_costs);
        break;
      default:
        break;
    }
    
    setResults(sortedResults);
  };

  const toggleBankSelection = (bankId) => {
    setSelectedBanks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(bankId)) {
        newSet.delete(bankId);
      } else {
        newSet.add(bankId);
      }
      return newSet;
    });
  };

  // Salvare manuală a calculului
  const handleManualSave = async () => {
    if (!saveClientName.trim()) {
      alert('Introduceți numele clientului');
      return;
    }

    setSavingManually(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/calculator/saved`,
        {
          client_name: saveClientName.trim(),
          loan_amount: parseFloat(formData.loanAmount),
          loan_period_months: parseInt(formData.loanPeriodMonths),
          property_price: formData.propertyPrice ? parseFloat(formData.propertyPrice) : null,
          property_evaluation: formData.propertyEvaluation ? parseFloat(formData.propertyEvaluation) : null,
          down_payment: formData.downPayment,
          down_payment_percent: formData.downPayment,
          product_type: buildProductType(formData),
          interest_type: formData.interestType,
          ircc_rate: parseFloat(formData.irccRate),
          income_collection: formData.incomeCollection,
          income_flow: formData.incomeFlow,
          life_insurance: formData.lifeInsurance,
          results: results.map(r => ({
            bank_name: r.bank_name,
            bank_id: r.bank_id,
            monthly_payment: r.monthly_payment_fixed_no_insurance || r.monthly_payment_variable_no_insurance,
            fixed_rate: r.fixed_rate,
            variable_rate: r.variable_rate,
            dae: r.dae,
            total_costs: r.total_costs
          })),
          selected_banks: Array.from(selectedBanks)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert('Calculul a fost salvat cu succes!');
      setShowSaveModal(false);
      setSaveClientName('');
    } catch (error) {
      console.error('Error saving calculation:', error);
      alert('Eroare la salvarea calculului');
    } finally {
      setSavingManually(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('ro-RO', {
      style: 'currency',
      currency: 'RON',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Formatare fără zecimale pentru costuri întregi (analiză, evaluare, PAD)
  const formatCurrencyNoDecimals = (amount) => {
    if (amount === null || amount === undefined) return '0 RON';
    // Dacă e număr întreg sau zecimalele sunt 0, afișează fără zecimale
    const num = parseFloat(amount);
    if (Number.isInteger(num) || num % 1 === 0) {
      return new Intl.NumberFormat('ro-RO').format(num) + ' RON';
    }
    return new Intl.NumberFormat('ro-RO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num) + ' RON';
  };

  // Formatare cu zecimale doar dacă sunt necesare (RNPM, asigurare)
  const formatCurrencySmartDecimals = (amount) => {
    if (amount === null || amount === undefined) return '0 RON';
    const num = parseFloat(amount);
    if (Number.isInteger(num) || num % 1 === 0) {
      return new Intl.NumberFormat('ro-RO').format(num) + ' RON';
    }
    return new Intl.NumberFormat('ro-RO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num) + ' RON';
  };

  const formatPercentage = (rate) => {
    if (rate === null || rate === undefined) return '0%';
    return `${rate}%`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleGenerateSchedule = (bankName, type) => {
    const selectedResult = results.find(r => r.bank_name === bankName);
    if (!selectedResult) return;

    // Deschide modalul PaymentSchedule
    setScheduleResult(selectedResult);
    setShowSchedule(true);
  };

  // Tab content
  if (activeTab === 'saved') {
    return (
      <div className="min-h-screen bg-gray-100">
        <div className="container mx-auto px-4 py-6">
          {/* Tab buttons */}
          <div className="bg-white rounded-lg shadow-sm p-4 mb-6 flex gap-4">
            <button
              onClick={() => setActiveTab('calculator')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                activeTab === 'calculator' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              🧮 Calculator Credit
            </button>
            <button
              onClick={() => setActiveTab('saved')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                activeTab === 'saved' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              💾 Calcule Salvate
            </button>
          </div>
          
          <SavedCalculations onLoadCalculation={(calc) => {
            setFormData({
              ...formData,
              loanAmount: calc.loan_amount,
              loanPeriodMonths: calc.loan_period_months,
              productType: calc.product_type,
              interestType: calc.interest_type,
              irccRate: calc.ircc_rate,
              incomeCollection: calc.income_collection,
              incomeFlow: calc.income_flow,
              lifeInsurance: calc.life_insurance
            });
            setClientName(calc.client_name);
            setResults(calc.results || []);
            setActiveTab('calculator');
          }} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="container mx-auto px-4 py-6">
        
        {/* Tab buttons */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6 flex gap-4">
          <button
            onClick={() => setActiveTab('calculator')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${
              activeTab === 'calculator' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            🧮 Calculator Credit
          </button>
          <button
            onClick={() => setActiveTab('saved')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${
              activeTab === 'saved' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            💾 Calcule Salvate
          </button>
        </div>

        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            🏦 Calculator Credit Ipotecar
          </h1>
          <p className="text-gray-600">Instrument profesional pentru analiza și compararea ofertelor de credit ipotecar</p>
          {results.length > 0 && (
            <div className="mt-3">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                ⚡ Actualizare în timp real activă
              </span>
            </div>
          )}
        </div>

        {/* 1. TIPUL PRODUSULUI */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">🏠 Tipul Produsului</h2>
          <div className="grid grid-cols-2 gap-3">
            {creditTypes.map(type => (
              <button
                key={type.value}
                onClick={() => setFormData(prev => ({ ...prev, creditType: type.value }))}
                className={`p-4 rounded-lg border-2 font-medium transition-all duration-200 ${
                  formData.creditType === type.value
                    ? type.color === 'green'
                      ? 'border-green-500 bg-green-50 text-green-700 shadow-md'
                      : 'border-blue-500 bg-blue-50 text-blue-700 shadow-md'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="text-2xl mb-2">{type.icon}</div>
                <div className="text-sm">{type.label}</div>
              </button>
            ))}
          </div>
        </div>

        {/* 2. CLASA ENERGETICĂ */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">🌿 Clasa Energetică</h2>
          <div className="grid grid-cols-3 gap-3">
            {energyClassTypes.map(type => (
              <button
                key={type.value}
                onClick={() => setFormData(prev => ({ ...prev, energyClass: type.value }))}
                className={`p-4 rounded-lg border-2 font-medium transition-all duration-200 ${
                  formData.energyClass === type.value
                    ? type.value === 'GREEN'
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700 shadow-md'
                      : type.value === 'A'
                        ? 'border-green-500 bg-green-50 text-green-700 shadow-md'
                        : 'border-blue-500 bg-blue-50 text-blue-700 shadow-md'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="text-2xl mb-2">{type.icon}</div>
                <div className="text-sm font-bold">{type.label}</div>
                <div className="text-xs text-gray-500 mt-1">{type.description}</div>
              </button>
            ))}
          </div>
        </div>

        {/* 3. TIPUL DOBÂNZII */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">📈 Tipul Dobânzii</h2>
          <div className="grid grid-cols-3 lg:grid-cols-6 gap-3">
            {interestTypes.map(type => (
              <button
                key={type.value}
                onClick={() => setFormData(prev => ({ ...prev, interestType: type.value }))}
                className={`p-4 rounded-lg border-2 font-medium transition-all duration-200 ${
                  formData.interestType === type.value
                    ? 'border-purple-500 bg-purple-50 text-purple-700 shadow-md'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="text-2xl mb-2">{type.icon}</div>
                <div className="text-sm">{type.label}</div>
              </button>
            ))}
          </div>
        </div>

        {/* 4. ÎNCASARE VENIT */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">💳 Încasare Venit</h2>
          <div className="grid grid-cols-2 gap-3">
            {incomeCollectionTypes.map(type => (
              <button
                key={type.value}
                onClick={() => setFormData(prev => ({ ...prev, incomeCollection: type.value }))}
                className={`p-4 rounded-lg border-2 font-medium transition-all duration-200 ${
                  formData.incomeCollection === type.value
                    ? type.color === 'green' 
                      ? 'border-green-500 bg-green-50 text-green-700 shadow-md'
                      : 'border-red-500 bg-red-50 text-red-700 shadow-md'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="text-2xl mb-2">{type.icon}</div>
                <div className="text-sm">{type.label}</div>
              </button>
            ))}
          </div>
        </div>

        {/* 5. AVANS & 6. ASIGURARE VIAȚĂ - Side by Side */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* 5. AVANS */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Avans</h2>
            <div className="grid grid-cols-2 gap-3">
              {downPaymentOptions.map(option => (
                <button
                  key={option.value}
                  data-testid={`dp-btn-${option.value}`}
                  onClick={() => handleDownPaymentSelect(option.value)}
                  className={`p-4 rounded-lg border-2 font-medium transition-all duration-200 ${
                    formData.downPayment === option.value
                      ? 'border-orange-500 bg-orange-50 text-orange-700 shadow-md'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="text-lg font-bold">{option.label}</div>
                  {getBaseValue(formData) > 0 && (
                    <div className="text-xs text-gray-500 mt-1">
                      {formatAmount(String(Math.round(getBaseValue(formData) * option.value / 100)))} RON
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* 6. ASIGURARE VIAȚĂ */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">🛡️ Asigurare de Viață</h2>
            <div className="grid grid-cols-2 gap-3">
              {lifeInsuranceTypes.map(type => (
                <button
                  key={type.value}
                  onClick={() => setFormData(prev => ({ ...prev, lifeInsurance: type.value }))}
                  className={`p-4 rounded-lg border-2 font-medium transition-all duration-200 ${
                    formData.lifeInsurance === type.value
                      ? type.color === 'green'
                        ? 'border-green-500 bg-green-50 text-green-700 shadow-md'
                        : 'border-red-500 bg-red-50 text-red-700 shadow-md'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="text-2xl mb-2">{type.icon}</div>
                  <div className="text-sm">{type.label}</div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Client Income Section - pentru discount-uri VIP */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">💰 Venit Client (Opțional)</h2>
          <p className="text-sm text-gray-500 mb-4">Completează venitul pentru a vedea produsele Premium și discount-urile VIP</p>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                👤 Venit Lunar Net Client (RON)
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={formatAmount(formData.clientIncome)}
                onChange={(e) => {
                  const val = e.target.value;
                  if (isValidAmountInput(val)) {
                    setFormData(prev => ({...prev, clientIncome: parseAmount(val)}));
                  }
                }}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors font-mono text-lg"
                placeholder="8.000"
              />
              <p className="text-xs text-gray-400 mt-1">💡 Produsele Premium necesită venit minim (ex: ≥10.000 lei)</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                👨‍👩‍👧 Venit Total Familie (RON)
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={formatAmount(formData.familyIncome)}
                onChange={(e) => {
                  const val = e.target.value;
                  if (isValidAmountInput(val)) {
                    setFormData(prev => ({...prev, familyIncome: parseAmount(val)}));
                  }
                }}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors font-mono text-lg"
                placeholder="15.000"
              />
              <p className="text-xs text-gray-400 mt-1">Suma veniturilor tuturor debitorilor</p>
            </div>
          </div>
        </div>

        {/* Property & Loan Parameters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">💼 Parametrii Creditului</h2>
          
          {/* Property inputs */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                🏠 Preț Imobil (RON)
              </label>
              <input
                type="text"
                inputMode="numeric"
                name="propertyPrice"
                value={formatAmount(formData.propertyPrice)}
                onChange={(e) => {
                  const val = e.target.value;
                  if (isValidAmountInput(val)) {
                    const raw = parseAmount(val);
                    setFormData(prev => ({
                      ...prev,
                      propertyPrice: raw,
                      // Evaluarea urmează prețul cât timp nu a fost modificată manual
                      propertyEvaluation: evaluationTouched ? prev.propertyEvaluation : raw
                    }));
                  }
                }}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors font-mono text-lg"
                placeholder="400.000"
              />
              <p className="text-xs text-gray-400 mt-1">Opțional - lasă gol dacă nu ai imobil identificat</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                📋 Evaluare Imobil (RON)
              </label>
              <input
                type="text"
                inputMode="numeric"
                name="propertyEvaluation"
                value={formatAmount(formData.propertyEvaluation)}
                onChange={(e) => {
                  const val = e.target.value;
                  if (isValidAmountInput(val)) {
                    const raw = parseAmount(val);
                    // Dacă golește câmpul, revine la auto-completarea din preț
                    setEvaluationTouched(raw !== '');
                    setFormData(prev => ({ ...prev, propertyEvaluation: raw === '' ? prev.propertyPrice : raw }));
                  }
                }}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors font-mono text-lg"
                placeholder="380.000"
              />
              <p className="text-xs text-gray-400 mt-1">Opțional – doar dacă o cunoști; altfel se folosește prețul. Avansul se raportează la minimul dintre preț și evaluare.</p>
            </div>
          </div>

          {/* Loan parameters */}
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                💰 Valoarea Creditului (RON) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                name="loanAmount"
                value={formatAmount(formData.loanAmount)}
                onChange={(e) => {
                  const val = e.target.value;
                  if (isValidAmountInput(val)) {
                    setFormData(prev => ({...prev, loanAmount: parseAmount(val)}));
                  }
                }}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors font-mono text-lg"
                placeholder="300.000"
                required
              />
              <p className="text-xs text-gray-400 mt-1">Introdu direct suma dorită</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Avans Disponibil (RON)
              </label>
              <input
                type="text"
                inputMode="numeric"
                name="downPaymentValue"
                data-testid="down-payment-value"
                value={formatAmount(formData.downPaymentValue)}
                onChange={(e) => {
                  const val = e.target.value;
                  if (isValidAmountInput(val)) {
                    handleDownPaymentValueChange(val);
                  }
                }}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors font-mono text-lg"
                placeholder="75.000"
              />
              {getBaseValue(formData) > 0 && formData.downPaymentValue && (
                <p className="text-xs mt-1 font-medium" style={{color: parseFloat(formData.downPaymentValue) / getBaseValue(formData) * 100 >= 20 ? '#16a34a' : '#ea580c'}}>
                  {(parseFloat(formData.downPaymentValue) / getBaseValue(formData) * 100).toFixed(1)}% din {getBaseValue(formData) < parseFloat(formData.propertyPrice || 0) ? 'evaluare' : 'pret imobil'}
                </p>
              )}
              {!formData.downPaymentValue && (
                <p className="text-xs text-gray-400 mt-1">Suma avansului in RON</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                ⏱️ Perioada Creditului (Luni) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="loanPeriodMonths"
                value={formData.loanPeriodMonths}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors font-mono text-lg"
                placeholder="300 (25 ani)"
                min="12"
                max="420"
                required
              />
              <div className="mt-1 text-xs text-gray-500">
                {formData.loanPeriodMonths && (
                  <span>≈ {Math.round(formData.loanPeriodMonths / 12 * 10) / 10} ani</span>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                📊 Rata IRCC (%)
              </label>
              <input
                type="number"
                step="0.01"
                name="irccRate"
                value={formData.irccRate}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors font-mono text-lg"
                placeholder="5.55"
                required
              />
            </div>
          </div>

          {/* Save calculation option */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <label className="flex items-center cursor-pointer mb-3">
              <input
                type="checkbox"
                checked={saveCalculation}
                onChange={(e) => setSaveCalculation(e.target.checked)}
                className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-3"
              />
              <span className="text-sm font-medium text-gray-700">
                💾 Salvează calculul pentru acest client
              </span>
            </label>
            
            {saveCalculation && (
              <input
                type="text"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                className="w-full md:w-1/2 px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                placeholder="Numele clientului"
              />
            )}
          </div>

          <div className="mt-6 text-center">
            <button
              onClick={handleSubmit}
              disabled={loading || !formData.loanAmount || !formData.loanPeriodMonths || isRealTimeUpdate}
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-4 px-8 rounded-lg shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Calculez ofertele...
                </span>
              ) : isRealTimeUpdate ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Actualizez în timp real...
                </span>
              ) : (
                '🔍 Analizează Băncile'
              )}
            </button>
          </div>
        </div>

        {/* Sort Filters */}
        {results.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">🎯 Filtrare și Sortare</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {sortFilters.map(filter => (
                <button
                  key={filter.value}
                  onClick={() => applySortFilter(filter.value)}
                  className={`p-3 rounded-lg border-2 font-medium transition-all duration-200 ${
                    sortFilter === filter.value
                      ? 'border-purple-500 bg-purple-50 text-purple-700 shadow-md'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <span className="mr-2">{filter.icon}</span>
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Results Table */}
        {results.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-green-600 to-teal-600 text-white p-6">
              <h2 className="text-2xl font-bold">📊 Comparația Băncilor</h2>
              <p className="mt-2 opacity-90">
                {formatCurrency(parseFloat(formData.loanAmount))} pe {formData.loanPeriodMonths} luni 
                ({Math.round(formData.loanPeriodMonths / 12 * 10) / 10} ani)
                {sortFilter && (
                  <span className="ml-4 px-3 py-1 bg-white/20 rounded-full text-sm">
                    Sortare: {sortFilters.find(f => f.value === sortFilter)?.label}
                  </span>
                )}
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full comparison-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">
                      Rubrica
                    </th>
                    {results.map((result, index) => (
                      <th key={index} className="px-4 py-4 text-center text-sm font-semibold text-gray-700 uppercase tracking-wider border-l">
                        <div className="flex flex-col items-center">
                          <span className={`mb-2 font-bold ${result.is_best_overall ? 'text-yellow-600' : ''}`}>
                            {result.is_best_overall && '⭐ '}
                            {result.bank_name}
                          </span>
                          <button
                            onClick={() => toggleBankSelection(result.bank_id)}
                            className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
                              selectedBanks.has(result.bank_id)
                                ? 'bg-blue-600 border-blue-600 text-white'
                                : 'border-gray-300 hover:border-blue-400'
                            }`}
                          >
                            {selectedBanks.has(result.bank_id) && '✓'}
                          </button>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {/* Fixed Rate Section */}
                  {formData.interestType !== 'variable' && (
                    <>
                      <tr className="bg-blue-50">
                        <td colSpan={results.length + 1} className="px-6 py-3 text-sm font-semibold text-blue-800">
                          🏠 Dobândă fixă și rate aferente
                        </td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 text-sm text-gray-700 font-medium">Dobândă fixă</td>
                        {results.map((result, index) => (
                          <td key={index} className="px-4 py-4 text-center text-sm text-gray-900 border-l font-semibold">
                            {formatPercentage(result.fixed_rate)}
                          </td>
                        ))}
                      </tr>
                      <tr className="bg-gray-50">
                        <td className="px-6 py-4 text-sm text-gray-700 font-medium">Rată fixă fără asigurare</td>
                        {results.map((result, index) => (
                          <td key={index} className={`px-4 py-4 text-center text-sm border-l font-semibold ${
                            result.is_best_rate ? 'bg-green-100 text-green-800' : 'text-gray-900'
                          }`}>
                            {result.is_best_rate && '🏆 '}
                            {formatCurrency(result.monthly_payment_fixed_no_insurance)}
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td className="px-6 py-4 text-sm text-gray-700 font-medium">Rată fixă cu asigurare</td>
                        {results.map((result, index) => (
                          <td key={index} className="px-4 py-4 text-center text-sm text-gray-900 border-l font-semibold">
                            {formatCurrency(result.monthly_payment_fixed_with_insurance)}
                          </td>
                        ))}
                      </tr>
                    </>
                  )}

                  {/* Variable Rate Section */}
                  {formData.interestType !== 'fixed_full' && (
                    <>
                      <tr className="bg-purple-50">
                        <td colSpan={results.length + 1} className="px-6 py-3 text-sm font-semibold text-purple-800">
                          📈 Dobândă variabilă și rate aferente
                        </td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 text-sm text-gray-700 font-medium">Dobândă variabilă</td>
                        {results.map((result, index) => (
                          <td key={index} className="px-4 py-4 text-center text-sm text-gray-900 border-l font-semibold">
                            {result.variable_rate ? `IRCC + ${(result.variable_rate - formData.irccRate).toFixed(2)}% = ${formatPercentage(result.variable_rate)}` : '-'}
                          </td>
                        ))}
                      </tr>
                      <tr className="bg-gray-50">
                        <td className="px-6 py-4 text-sm text-gray-700 font-medium">Rată variabilă fără asigurare</td>
                        {results.map((result, index) => (
                          <td key={index} className={`px-4 py-4 text-center text-sm border-l font-semibold ${
                            formData.interestType === 'variable' && result.is_best_rate ? 'bg-green-100 text-green-800' : 'text-gray-900'
                          }`}>
                            {formData.interestType === 'variable' && result.is_best_rate && '🏆 '}
                            {formatCurrency(result.monthly_payment_variable_no_insurance)}
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td className="px-6 py-4 text-sm text-gray-700 font-medium">Rată variabilă cu asigurare</td>
                        {results.map((result, index) => (
                          <td key={index} className="px-4 py-4 text-center text-sm text-gray-900 border-l font-semibold">
                            {formatCurrency(result.monthly_payment_variable_with_insurance)}
                          </td>
                        ))}
                      </tr>
                    </>
                  )}

                  {/* Costs Section */}
                  <tr className="bg-red-50">
                    <td colSpan={results.length + 1} className="px-6 py-3 text-sm font-semibold text-red-800">
                      💰 Costuri credit
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-sm text-gray-700 font-medium">Comision analiză dosar</td>
                    {results.map((result, index) => (
                      <td key={index} className="px-4 py-4 text-center text-sm text-gray-900 border-l">
                        {formatCurrencyNoDecimals(result.cost_breakdown?.analysis_fee)}
                      </td>
                    ))}
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-700 font-medium">Comision administrare lunar</td>
                    {results.map((result, index) => (
                      <td key={index} className="px-4 py-4 text-center text-sm text-gray-900 border-l">
                        {formatCurrencyNoDecimals(result.cost_breakdown?.admin_fee_monthly)}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-sm text-gray-700 font-medium">RNPM</td>
                    {results.map((result, index) => (
                      <td key={index} className="px-4 py-4 text-center text-sm text-gray-900 border-l">
                        {formatCurrencySmartDecimals(result.cost_breakdown?.rnpm)}
                      </td>
                    ))}
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-700 font-medium">Taxă evaluare apartament</td>
                    {results.map((result, index) => (
                      <td key={index} className="px-4 py-4 text-center text-sm text-gray-900 border-l">
                        {formatCurrencyNoDecimals(result.cost_breakdown?.apartment_evaluation)}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-sm text-gray-700 font-medium">Taxă evaluare casă</td>
                    {results.map((result, index) => (
                      <td key={index} className="px-4 py-4 text-center text-sm text-gray-900 border-l">
                        {formatCurrencyNoDecimals(result.cost_breakdown?.house_evaluation)}
                      </td>
                    ))}
                  </tr>

                  {/* Insurance Section */}
                  <tr className="bg-blue-50">
                    <td colSpan={results.length + 1} className="px-6 py-3 text-sm font-semibold text-blue-800">
                      🛡️ Asigurări
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-sm text-gray-700 font-medium">Asigurare viață (% lunar la sold)</td>
                    {results.map((result, index) => (
                      <td key={index} className="px-4 py-4 text-center text-sm text-gray-900 border-l">
                        {formatPercentage(result.insurance_costs?.life_insurance_rate)}
                      </td>
                    ))}
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-700 font-medium">PAD</td>
                    {results.map((result, index) => (
                      <td key={index} className="px-4 py-4 text-center text-sm text-gray-900 border-l">
                        {formatCurrencyNoDecimals(result.insurance_costs?.pad_insurance)}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-sm text-gray-700 font-medium">Asigurare imobil facultativă</td>
                    {results.map((result, index) => (
                      <td key={index} className="px-4 py-4 text-center text-sm text-gray-900 border-l">
                        {formatCurrencySmartDecimals(result.insurance_costs?.property_insurance)}
                      </td>
                    ))}
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-700 font-medium">Comision rambursare anticipată</td>
                    {results.map((result, index) => (
                      <td key={index} className="px-4 py-4 text-center text-sm text-gray-900 border-l">
                        {formatPercentage(result.cost_breakdown?.early_repayment_fee)}
                      </td>
                    ))}
                  </tr>

                  {/* DAE */}
                  <tr className="bg-yellow-50">
                    <td className="px-6 py-4 text-sm text-gray-700 font-bold">DAE</td>
                    {results.map((result, index) => (
                      <td key={index} className={`px-4 py-4 text-center text-sm font-bold border-l ${
                        result.is_best_dae ? 'bg-green-100 text-green-800' : 'text-gray-900'
                      }`}>
                        {result.is_best_dae && '🏆 '}
                        {formatPercentage(result.dae)}
                      </td>
                    ))}
                  </tr>

                  {/* Other Information */}
                  <tr className="bg-gray-50">
                    <td colSpan={results.length + 1} className="px-6 py-3 text-sm font-semibold text-gray-800">
                      ℹ️ Alte informații
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-sm text-gray-700 font-medium">Valabilitate aprobare</td>
                    {results.map((result, index) => (
                      <td key={index} className="px-4 py-4 text-center text-sm text-gray-900 border-l">
                        {result.approval_validity_days} zile
                      </td>
                    ))}
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-700 font-medium">Acțiuni</td>
                    {results.map((result, index) => (
                      <td key={index} className="px-4 py-4 text-center border-l">
                        <div className="space-y-2">
                          <button 
                            onClick={() => handleGenerateSchedule(result.bank_name, 'detailed')}
                            className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded-lg text-xs font-medium transition-colors w-full"
                          >
                            📋 Scadentar
                          </button>
                          <button 
                            onClick={() => handleGenerateSchedule(result.bank_name, 'summary')}
                            className="bg-indigo-500 hover:bg-indigo-600 text-white px-3 py-2 rounded-lg text-xs font-medium transition-colors w-full"
                          >
                            📊 Scadentar Sumar
                          </button>
                        </div>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Selected Banks Actions */}
            {selectedBanks.size > 0 && (
              <div className="bg-gray-50 border-t p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  📌 Bănci selectate ({selectedBanks.size}): 
                  <span className="text-blue-600 ml-2">
                    {results.filter(r => selectedBanks.has(r.bank_id)).map(r => r.bank_name).join(', ')}
                  </span>
                </h3>
                <div className="flex flex-wrap gap-4">
                  <button className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors">
                    📋 Scadentar Comparativ
                  </button>
                  <button 
                    onClick={() => setShowPreview(true)}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                  >
                    📄 Generează Ofertă PDF
                  </button>
                  <button 
                    onClick={() => setShowSaveModal(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                  >
                    💾 Salvează Calculul
                  </button>
                </div>

                {/* FEIS Generation - Select individual bank */}
                {selectedBanks.size >= 1 && (
                  <div className="mt-6 pt-6 border-t">
                    <h4 className="text-md font-semibold text-gray-700 mb-3">
                      📋 Generare FEIS (Fișă Europeană) - Selectează o bancă:
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {results.filter(r => selectedBanks.has(r.bank_id)).map(result => (
                        <button
                          key={result.bank_id}
                          onClick={() => {
                            setSelectedOfferForFEIS(result);
                            setShowFEIS(true);
                          }}
                          className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg font-medium transition-colors text-sm"
                        >
                          📄 FEIS - {result.bank_name}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      * FEIS se generează individual pentru fiecare bancă aleasă de client
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Save button when no banks selected */}
            {selectedBanks.size === 0 && (
              <div className="bg-gray-50 border-t p-6">
                <button 
                  onClick={() => setShowSaveModal(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  💾 Salvează Calculul
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Offer Preview Modal */}
      {showPreview && (
        <ErrorBoundary
          message="Oferta PDF nu a putut fi afișată. Verificați datele introduse și încercați din nou."
          onReset={() => setShowPreview(false)}
        >
          <OfferPreview
            open={showPreview}
            onClose={() => setShowPreview(false)}
            results={results.filter(r => selectedBanks.has(r.bank_id))}
            clientName={clientName}
            loanAmount={formData.loanAmount}
            loanPeriodMonths={formData.loanPeriodMonths}
            interestType={formData.interestType}
            productType={buildProductType(formData)}
            irccRate={formData.irccRate}
            propertyPrice={formData.propertyPrice}
            downPayment={formData.downPayment}
          />
        </ErrorBoundary>
      )}

      {/* Save Calculation Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-gray-800 mb-4">💾 Salvează Calculul</h3>
            <p className="text-gray-600 mb-4">
              Introduceți numele clientului pentru a salva acest calcul. 
              Calculele se șterg automat după 6 luni.
            </p>
            
            <input
              type="text"
              value={saveClientName}
              onChange={(e) => setSaveClientName(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none mb-4"
              placeholder="Numele clientului"
              autoFocus
            />
            
            <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm">
              <div className="grid grid-cols-2 gap-2 text-gray-600">
                <span>Credit:</span>
                <span className="font-medium">{formatCurrency(formData.loanAmount)}</span>
                <span>Perioadă:</span>
                <span className="font-medium">{Math.round(formData.loanPeriodMonths / 12)} ani</span>
                <span>Tip:</span>
                <span className="font-medium">{creditTypes.find(p => p.value === formData.creditType)?.label}</span>
                <span>Dobândă:</span>
                <span className="font-medium">{interestTypes.find(i => i.value === formData.interestType)?.label}</span>
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowSaveModal(false);
                  setSaveClientName('');
                }}
                className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Anulează
              </button>
              <button
                onClick={handleManualSave}
                disabled={savingManually || !saveClientName.trim()}
                className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingManually ? 'Se salvează...' : '💾 Salvează'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FEIS Generator Modal */}
      {showFEIS && selectedOfferForFEIS && (
        <ErrorBoundary
          message="Documentul FEIS nu a putut fi afișat. Verificați datele introduse și încercați din nou."
          onReset={() => {
            setShowFEIS(false);
            setSelectedOfferForFEIS(null);
          }}
        >
          <FEISGenerator
            open={showFEIS}
            onClose={() => {
              setShowFEIS(false);
              setSelectedOfferForFEIS(null);
            }}
            selectedOffer={selectedOfferForFEIS}
            loanAmount={formData.loanAmount}
            loanPeriodMonths={formData.loanPeriodMonths}
            interestType={formData.interestType}
            productType={buildProductType(formData)}
            irccRate={formData.irccRate}
            propertyPrice={formData.propertyPrice}
            downPayment={formData.downPayment}
          />
        </ErrorBoundary>
      )}

      {/* Modal Scadențar */}
      {scheduleResult && (
        <PaymentSchedule
          result={scheduleResult}
          loanAmount={parseFloat(formData.loanAmount)}
          loanPeriodMonths={parseInt(formData.loanPeriodMonths)}
          fixedPeriodYears={
            formData.interestType === 'fixed_2y' ? 2 :
            formData.interestType === 'fixed_3y' ? 3 :
            formData.interestType === 'fixed_5y' ? 5 :
            formData.interestType === 'fixed_10y' ? 10 :
            formData.interestType === 'fixed_full' ? Math.ceil(parseInt(formData.loanPeriodMonths) / 12) : 0
          }
          fixedRate={scheduleResult.fixed_rate}
          variableRate={scheduleResult.variable_rate}
          lifeInsuranceRate={scheduleResult.insurance_costs?.life_insurance_rate || 0.026}
          isOpen={showSchedule}
          onClose={() => setShowSchedule(false)}
        />
      )}
    </div>
  );
};

export default CreditCalculator;
